import cx from 'classnames';
import { ImageProps } from 'next/image';
import { HTMLAttributeAnchorTarget, useCallback } from 'react';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { FacetCard as FacetCardFromDesignSystem } from '../../../design-system/components/card/facet/FacetCard';
import { imageLoaderForId } from '../../../domains/Layout/Image/URL/ImageURL';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { Facet } from '../../../resources/facet/domain/types/facet';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { IntrinsicElement } from '../../utilities/types/IntrinsicElement';
import { PickDomain } from '../../utilities/types/PickDomain';

export type Props = {
    facet: PickDomain<
        Facet,
        'id' | 'squareImage' | 'coverImage' | 'slug' | 'title',
        {
            destination: Pick<Destination, 'slug'>;
        }
    >;
    small?: boolean;
    image: Partial<ImageProps> & {
        sizes: NonNullable<ImageProps['sizes']>;
    };
    trackingInformation: {
        click: TrackingProperties<typeof ampli.facetClicked>;
    };
    target?: HTMLAttributeAnchorTarget;
    titleElement?: IntrinsicElement;
};
export function FacetCard({
    facet,
    image,
    trackingInformation,
    target,
    titleElement,
    small,
}: Props) {
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const handleClick = useCallback(() => {
        trackEvent((ampli, defaultProperties) =>
            ampli.facetClicked({
                ...defaultProperties,
                ...trackingInformation.click,
            }),
        );
    }, [trackEvent, trackingInformation.click]);

    return (
        <FacetCardFromDesignSystem
            image={{
                ...image,
                alt:
                    (facet.squareImage
                        ? facet.squareImage.legend.toString()
                        : facet.coverImage.legend.toString()) || facet.title,
                fill: true,
                loader: imageLoaderForId({
                    aspectRatio: FacetCardFromDesignSystem.PICTURE_RATIO,
                }),
                className: cx(image.className, 'imageCover'),
                src: facet.squareImage
                    ? facet.squareImage.id.toString()
                    : facet.coverImage.id.toString(),
            }}
            title={facet.title}
            href={generatePath('facet', {
                facetSlug: facet.slug,
                destinationSlug: facet.destination.slug,
            })}
            target={target}
            onClick={handleClick}
            titleElement={titleElement}
            small={small}
        />
    );
}
